import { getResourceString, UPDATE_SCHEMA } from '@takeshape/actions';
import {
  BranchMergeOperation,
  openBranchMerge,
  type SetCurrentBranchPayload,
  setCurrentBranch
} from 'app/branches/actions';
import { DEFAULT_BRANCH, SCRATCH_BRANCH } from 'app/branches/constants';
import { useActiveSchema } from 'app/branches/hooks/use-active-schema';
import { useBranchPermissions } from 'app/branches/hooks/use-branch-permissions';
import { createBranchRoutine } from 'app/branches/sagas/create-branch';
import { deleteBranchRoutine } from 'app/branches/sagas/delete-branch';
import { getCurrentBranchCanPromote, getCurrentBranchNeedsUpdate, getState } from 'app/branches/selectors';
import { showConfirm } from 'app/confirm';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useInit from '../../../common/hooks/use-init.js';
import useRouter from '../../../common/hooks/use-router.js';
import useWatch from '../../../common/hooks/use-watch.js';
import { usePermission } from '../../../permissions/hooks.js';
import { getCurrentProjectId } from '../../../project/selectors.js';
import { useBranch } from '../../hooks/use-branch.js';
import { checkBranchStatus } from '../../sagas/check-branch-status.js';
import { fetchSchemaRoutine } from '../../sagas/fetch-schema.js';
import type { BranchSwitcherProps } from './branch-switcher.js';

const invalidBranchNameStrings = ['#', '~', '^', '?', '*', '[', '..', '\\'];

export function validateBranchName(value: string) {
  for (const str of invalidBranchNameStrings) {
    if (value.includes(str)) return `Branch names cannot contain ${str}`;
  }

  // Cannot be the single @ character
  if (/^@$/.exec(value)) return 'Branch names cannot be "@"';
  // Cannot start or end with /
  if (/(^\/)|(\/$)/.exec(value)) return 'Branch names cannot begin or end with /';
  // Cannot start with a -
  if (/^-/.exec(value)) return 'Branch names cannot start with -';
  // Cannot end with a .
  if (/\.$/.exec(value)) return 'Branch names cannot end with .';
}

export function useBranchSwitcher(): BranchSwitcherProps {
  const dispatch = useDispatch();
  const projectId = useSelector(getCurrentProjectId);
  const branchState = useSelector(getState);
  const currentBranchNeedsUpdate = useSelector(getCurrentBranchNeedsUpdate);
  const currentBranchCanPromote = useSelector(getCurrentBranchCanPromote);
  const branchPermissions = useBranchPermissions();
  const canUpdateSchema = usePermission(getResourceString('project', { projectId }), UPDATE_SCHEMA);
  const { currentBranch: currentBranchName } = branchState;
  const currentBranch = useBranch(currentBranchName);
  const scratchBranch = useBranch(SCRATCH_BRANCH);
  const { dirty, reset } = useActiveSchema();
  const router = useRouter();

  const switchBranch = useCallback(
    (payload: SetCurrentBranchPayload) => {
      const { branch } = payload;
      const { data: userLayerSchema } = branchState.branches[branch].layers.user;
      dispatch(setCurrentBranch(payload));
      dispatch(checkBranchStatus.requested());

      reset();
      if (!userLayerSchema) {
        dispatch(fetchSchemaRoutine.requested(undefined, { branch, runtime: false }));
      }
    },
    [branchState.branches, dispatch, reset]
  );

  // Handle init and project change
  useWatch({
    value: projectId,
    onChange: useCallback(() => {
      currentBranch.fetchSchema({ runtime: false });
      scratchBranch.fetchSchema({ runtime: false });
    }, [currentBranch, scratchBranch]),
    runOnInit: true
  });

  // Handle history navigation
  useWatch({
    value: router.params?.branch,
    onChange: useCallback(
      (routerBranchParam: string | undefined) => {
        if (routerBranchParam && routerBranchParam !== branchState.currentBranch) {
          switchBranch({ branch: decodeURIComponent(routerBranchParam) });
        }
      },
      [branchState.currentBranch, switchBranch]
    )
  });

  // Check if current branch needs update on init
  useInit({
    onInit: useCallback(() => {
      dispatch(checkBranchStatus.requested());
    }, [dispatch])
  });

  const handleSetCurrentBranch = useCallback(
    (payload: SetCurrentBranchPayload) => {
      return () => {
        if (dirty) {
          dispatch(
            showConfirm({
              type: 'delete',
              title: 'Undeployed Changes',
              message:
                'You have not yet deployed changes to this branch. If you switch branches, you will lose these changes.',
              okLabel: `Switch to "${payload.branch}"`,
              onConfirm(): void {
                switchBranch(payload);
              }
            })
          );
        } else {
          switchBranch(payload);
        }
      };
    },
    [dirty, dispatch, switchBranch]
  );

  const handleUpdateCurrentBranch = useCallback(() => {
    if (currentBranchName === DEFAULT_BRANCH || !canUpdateSchema) return;
    dispatch(
      openBranchMerge({
        branchName: currentBranchName,
        operation: BranchMergeOperation.Update
      })
    );
  }, [canUpdateSchema, currentBranchName, dispatch]);

  const handlePromoteCurrentBranch = useCallback(() => {
    if (currentBranchName === DEFAULT_BRANCH || !canUpdateSchema) return;
    dispatch(
      openBranchMerge({
        branchName: currentBranchName,
        operation: BranchMergeOperation.Promote
      })
    );
  }, [canUpdateSchema, currentBranchName, dispatch]);

  const createBranch = useCallback(
    (name: string) => {
      setSearch('');
      dispatch(createBranchRoutine.requested(undefined, { name }));
      reset();
    },
    [dispatch, reset]
  );

  const handleCreateBranch = useCallback(
    (name: string) => {
      return () => {
        if (!branchPermissions.canCreate) return;
        if (dirty) {
          dispatch(
            showConfirm({
              type: 'delete',
              title: 'Undeployed Changes',
              message:
                'You have not yet deployed changes to this branch. If you create a new branch, you will lose these changes.',
              okLabel: `Create "${name}"`,
              onConfirm(): void {
                createBranch(name);
              }
            })
          );
        } else {
          createBranch(name);
        }
      };
    },
    [branchPermissions.canCreate, dirty, dispatch, createBranch]
  );

  const handleDeleteBranch = useCallback(
    (name: string) => {
      return (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!branchPermissions.canDelete) return;
        dispatch(
          showConfirm({
            type: 'delete',
            title: 'Delete Branch',
            message: `Are you sure you want to delete ${name}?`,
            okLabel: 'Delete',
            onConfirm(): void {
              dispatch(deleteBranchRoutine.requested(undefined, { name }));
            }
          })
        );
      };
    },
    [branchPermissions.canDelete, dispatch]
  );

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string>();

  const branches = useMemo(() => {
    return Object.keys(branchState.branches);
  }, [branchState.branches]);

  const unfilteredBranches = useMemo(
    () =>
      branches.filter((branch) => branch.includes(search) && branch !== SCRATCH_BRANCH && branch !== currentBranchName),
    [branches, search, currentBranchName]
  );

  const branchExists = useMemo(() => branches.includes(search), [branches, search]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.key !== 'Escape') {
        // Prevents autoselecting item while typing (default Menu behaviour)
        e.stopPropagation();
      }

      if (e.key === 'Enter' && !branchExists && !error && branchPermissions.canCreate) {
        // Create a new branch if it doesn't already exist
        handleCreateBranch(search)();
      }
    },
    [branchExists, branchPermissions.canCreate, error, handleCreateBranch, search]
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback((event) => {
    setError(validateBranchName(event.currentTarget.value));
    setSearch(event.currentTarget.value);
  }, []);

  const currentBranchLoading = useMemo(() => {
    const branchStateLoading = Boolean(branchState.branches[branchState.currentBranch]?.runtimeSchema.loading);

    return branchStateLoading || currentBranchNeedsUpdate === null || currentBranchCanPromote === null;
  }, [branchState.branches, branchState.currentBranch, currentBranchCanPromote, currentBranchNeedsUpdate]);

  return {
    branches,
    currentBranch: branchState.currentBranch,
    currentBranchLoading,
    handleSetCurrentBranch,
    handleUpdateCurrentBranch,
    handlePromoteCurrentBranch,
    handleCreateBranch,
    handleDeleteBranch,
    branchPermissions,
    handleKeyDown,
    handleChange,
    unfilteredBranches,
    branchExists,
    error,
    search,
    currentBranchNeedsUpdate,
    currentBranchCanPromote
  };
}
